import { useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/StatusBadge";
import { Button, buttonClassName } from "@/components/ui/Button";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckIcon,
  ClockIcon,
  ExternalLinkIcon,
  InfoCircleIcon,
} from "@/components/ui/icons";
import { Input } from "@/components/ui/Input";
import { Popover } from "@/components/ui/Popover";
import type { AlertInstanceResource } from "@/lib/api-types";
import type { BadgeFill } from "@/lib/status-badge";
import { cx } from "@/lib/cx";

/**
 * DESIGN.md § Components `alert-row`: 72px minimum, a 3px leading stripe
 * carrying severity, the alert sentence at `{typography.body}`, the entity
 * and date at `{typography.meta}` beneath. The frozen story spec's own
 * Boundaries widen the row's action count from DESIGN.md's one ("the
 * acknowledge action") to exactly three, always in this order:
 * acknowledge, snooze, record link (VC-3.9-02) — the later, more specific
 * source wins.
 *
 * Shared between the alert centre (Story 3.9) and, per EXPERIENCE.md's own
 * IA table, the dashboard's future "Needs attention now" panel — a single
 * component, not a route-specific one, the same "one shared primitive"
 * discipline `DataTable`/`ConflictBanner`/`EmptyState` already established.
 *
 * **Criticality icon+badge, and a property line (Owner-requested,
 * 2026-08-29): "the alerts are not very meaningful... can we have the
 * property address, more concise and meaningful display and... assign it a
 * criticality with a color [or] icon."** The 3px stripe stays exactly as
 * DESIGN.md specifies it — never colour alone, EXPERIENCE.md's own
 * Accessibility Floor, and every existing `w-alert-row-stripe`/severity-text
 * assertion (VC-3.9-02) still holds unchanged. The icon+`Badge` pair reuses
 * `StatusBadge.tsx`'s own shared primitive directly (`fill: "overdue"`/
 * `"attention"`/`"neutral"` — its own doc comment already anticipated a
 * second caller beyond the user list), never a second, alert-row-owned
 * badge implementation. `property_address`/`property_nickname` come from
 * `app.domain.alerting.entity_context` (backend) — `None` for a rule not
 * genuinely property-scoped (`backup-failed`, `email-delivery-failure`,
 * `weekly-heartbeat`), honestly omitted from the meta line rather than
 * shown as a placeholder.
 */

const SEVERITY_LABELS: Record<string, string> = {
  overdue: "Overdue",
  attention: "Needs attention",
  informational: "Informational",
};

const SEVERITY_BADGE_FILLS: Record<string, BadgeFill> = {
  overdue: "overdue",
  attention: "attention",
  informational: "neutral",
};

const SEVERITY_ICONS: Record<string, typeof AlertTriangleIcon> = {
  overdue: AlertTriangleIcon,
  attention: AlertCircleIcon,
  informational: InfoCircleIcon,
};

const SEVERITY_ICON_CLASSES: Record<string, string> = {
  overdue: "text-danger",
  attention: "text-warning",
  informational: "text-ink-tertiary",
};

/** DESIGN.md's own `alert-row.stripe-overdue/attention/informational` are
 * themselves `{colors.danger}`/`{colors.warning}`/`{colors.border-strong}`
 * — no new color token needed, just the existing ones by name. */
const SEVERITY_STRIPE_CLASSES: Record<string, string> = {
  overdue: "bg-danger",
  attention: "bg-warning",
  informational: "bg-border-strong",
};

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "America/New_York",
});

function isoDateNDaysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export interface AlertRowProps {
  alert: AlertInstanceResource;
  onAcknowledge: (alert: AlertInstanceResource) => void;
  onSnooze: (alert: AlertInstanceResource, until: string) => void;
  isAcknowledging?: boolean;
  isSnoozing?: boolean;
  /** Edge matrix: "no destructive/mutating affordance" for a Read-only
   * account — acknowledge/snooze both count (this story's own judgement
   * call, matching `POST .../acknowledge|snooze`'s `role=Role.MEMBER`
   * gate). Withheld by absence, not `disabled` (DESIGN.md's own rule); the
   * record-link action is unaffected — viewing is not a mutation. Defaults
   * to `false` — the same "safer failure mode while the actor is unknown"
   * default `useIsReadOnly` itself establishes — so a caller that forgets
   * to pass it never briefly shows a control it should have withheld. */
  canMutate?: boolean;
}

export function AlertRow({
  alert,
  onAcknowledge,
  onSnooze,
  isAcknowledging = false,
  isSnoozing = false,
  canMutate = false,
}: AlertRowProps) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [customDate, setCustomDate] = useState("");

  function pick(until: string) {
    setSnoozeOpen(false);
    setCustomDate("");
    onSnooze(alert, until);
  }

  // Severity is still real text, not colour alone (EXPERIENCE.md's own
  // Accessibility Floor) — it now lives in the `Badge` label above the
  // sentence instead of repeating a second time in this line, so `meta`
  // leads with the property (an honest omission when the rule genuinely
  // isn't property-scoped) ahead of the entity link and the date.
  const meta = [
    alert.property_address ?? alert.property_nickname ?? undefined,
    alert.entity_label ?? undefined,
    DATE_FORMATTER.format(new Date(alert.created_at)),
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
  const severityFill = SEVERITY_BADGE_FILLS[alert.severity] ?? "attention";
  const SeverityIcon = SEVERITY_ICONS[alert.severity] ?? AlertCircleIcon;
  const severityIconClass = SEVERITY_ICON_CLASSES[alert.severity] ?? "text-warning";

  return (
    <li className="flex min-h-alert-row flex-col border-b border-border-hairline bg-surface md:flex-row md:items-center md:gap-4 md:pr-2">
      {/* Stripe + sentence stay their own inner row regardless of the
       * outer row's direction, so the stripe keeps reading as a vertical
       * accent bar rather than stretching sideways on phone. Live-caught:
       * the actions column below used to compete for the same horizontal
       * space as the sentence with `shrink-0` on both sides, squeezing the
       * sentence into a many-line, 579-849px-tall column on a phone width —
       * it now gets its own full-width row underneath instead. */}
      <div className="flex min-w-0 flex-1 gap-4">
        {/* The 3px severity stripe — color carries the signal, but never
         * alone: the same severity is spelled out in the `Badge` label
         * below as well (DESIGN.md: "every alert row shows its severity in
         * text as well as in its stripe"). */}
        <span
          aria-hidden="true"
          className={cx(
            "w-alert-row-stripe shrink-0 self-stretch",
            SEVERITY_STRIPE_CLASSES[alert.severity] ?? SEVERITY_STRIPE_CLASSES.attention,
          )}
        />
        <Link to={`/alerts/${alert.id}`} className="min-w-0 flex-1 py-3 text-ink no-underline hover:underline">
          <div className="flex items-center gap-1.5">
            <SeverityIcon className={cx("shrink-0", severityIconClass)} />
            <Badge label={SEVERITY_LABELS[alert.severity] ?? alert.severity} fill={severityFill} />
          </div>
          <p className="mt-1.5 text-body text-ink">{alert.sentence}</p>
          <p className="mt-1 text-meta text-ink-secondary">{meta}</p>
        </Link>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1 px-2 pb-3 md:justify-end md:px-0 md:pb-0">
        {canMutate ? (
          <>
            {/* Action 1 of 3, in order: acknowledge ("I've got it", R-A3). */}
            <Button type="button" variant="ghost" disabled={isAcknowledging} onClick={() => onAcknowledge(alert)}>
              <CheckIcon />
              I&rsquo;ve got it
            </Button>
            {/* Action 2 of 3: snooze ("Follow up in…", R-N7 — reschedules, never dismisses). */}
            <Popover
              open={snoozeOpen}
              onOpenChange={setSnoozeOpen}
              trigger={
                <Button type="button" variant="ghost" disabled={isSnoozing}>
                  <ClockIcon />
                  Follow up in&hellip;
                </Button>
              }
            >
              <div className="flex w-64 flex-col gap-2 p-1">
                <Button type="button" variant="secondary" onClick={() => pick(isoDateNDaysFromNow(1))}>
                  Tomorrow
                </Button>
                <Button type="button" variant="secondary" onClick={() => pick(isoDateNDaysFromNow(7))}>
                  Next week
                </Button>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    aria-label="Follow up on a specific date"
                    value={customDate}
                    onChange={(event) => setCustomDate(event.target.value)}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={!customDate}
                    onClick={() => customDate && pick(customDate)}
                  >
                    Set
                  </Button>
                </div>
              </div>
            </Popover>
          </>
        ) : null}
        {/* Action 3 of 3: the record link (AD-27's registry — never a
         * screen-owned label/URL). Absent, structurally, only when the
         * registry itself has nothing to link to (this story's own
         * documented fallback for the "system" sentinel every real Phase
         * 1a rule carries today) — still always the third slot when it is
         * present, never reordered. */}
        {alert.entity_url ? (
          <Link to={alert.entity_url} className={buttonClassName("ghost")}>
            <ExternalLinkIcon />
            {alert.entity_label ?? "View record"}
          </Link>
        ) : null}
      </div>
    </li>
  );
}
